import {
  EnvironmentOutlined,
  EyeOutlined,
  GlobalOutlined,
  SaveOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Autocomplete,
  GoogleMap,
  LoadScript,
  Marker,
} from '@react-google-maps/api';
import { useIntl } from '@umijs/max';
import {
  Button,
  Card,
  Col,
  Divider,
  Input,
  message,
  Row,
  Select,
  Space,
  Typography,
} from 'antd';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { generateUploadUrl, uploadFile } from '@/services/admin/file';

const { Text, Paragraph } = Typography;

// 语言配置
const LANGUAGE_CONFIGS = {
  'zh-CN': { label: '中文', code: 'zh-CN' },
  en: { label: 'English', code: 'en' },
  km: { label: 'ភាសាខ្មែរ', code: 'km' },
} as const;

type LanguageCode = keyof typeof LANGUAGE_CONFIGS;

// 地区配置
const REGION_CONFIGS = {
  cn: { label: '中国', code: 'cn', center: { lat: 39.9042, lng: 116.4074 } }, // 北京
  us: { label: '美国', code: 'us', center: { lat: 40.7128, lng: -74.006 } }, // 纽约
  kh: { label: '柬埔寨', code: 'kh', center: { lat: 11.5564, lng: 104.9282 } }, // 金边
} as const;

type RegionCode = keyof typeof REGION_CONFIGS;

// 地图容器样式
const mapContainerStyle = {
  width: '100%',
  height: '500px',
};

// 默认地图中心（柬埔寨金边，与默认地区 kh 一致）
const defaultCenter: google.maps.LatLngLiteral = REGION_CONFIGS.kh.center;

// Google Maps 库配置：只恢复 Places Autocomplete 联想，不再做 Places Details 额外请求。
const googleMapsLibraries: 'places'[] = ['places'];
const autocompleteFields = [
  'place_id',
  'name',
  'formatted_address',
  'geometry',
  'address_components',
];

// 序列化的地点数据结构（用于回显）
export interface SerializedPlaceData {
  /** Place ID（仅用于保存基础标识，不再用它主动请求 Places Details） */
  place_id?: string | null;
  /** 地点名称（优先用于回填输入框） */
  name?: string | null;
  /** 格式化地址 */
  formatted_address?: string | null;
  /** 附近区域 */
  vicinity?: string | null;
  /** 纬度（用于回显地图位置） */
  latitude?: number | null;
  /** 经度（用于回显地图位置） */
  longitude?: number | null;
  /** 视口边界 */
  viewport?: {
    south?: number | null;
    west?: number | null;
    north?: number | null;
    east?: number | null;
  } | null;
  /** 地址组件（可选，用于构建地址信息） */
  address_components?: Array<{
    long_name?: string;
    short_name?: string;
    types?: string[];
  }> | null;
  /** 详细地址（用户手动填写的详细字段） */
  detail?: string | null;
}

export interface GoogleMapLocationPickerProps {
  /** 选中的地点变化回调 */
  onPlaceChange?: (place: google.maps.places.PlaceResult | null) => void;
  /** 地图预览图上传完成后的 URL 回调 */
  onAddressPreviewUrlChange?: (url: string | null) => void;
  /** 地图预览图上传状态回调 */
  onAddressPreviewUploadingChange?: (uploading: boolean) => void;
  /** 详细地址变化回调 */
  onDetailAddressChange?: (address: string) => void;
  /** 初始选中的地点（PlaceResult 对象，优先级最高） */
  initialPlace?: google.maps.places.PlaceResult | null;
  /** 初始详细地址 */
  initialDetailAddress?: string;
  /** 初始地点数据（序列化格式，用于回显，当没有 initialPlace 时使用） */
  initialPlaceData?: SerializedPlaceData | null;
  /** 初始结构化地址（API.StructuredAddress，组件内部会自动转换为 SerializedPlaceData） */
  initialStructuredAddress?: API.StructuredAddress | null;
  /** 是否禁用（预览/只读模式） */
  disabled?: boolean;
}

export interface GoogleMapLocationPickerHandle {
  ensureAddressPreview: () => Promise<string | null>;
}

const getPlaceLocation = (
  place: google.maps.places.PlaceResult | null,
): google.maps.LatLngLiteral | null => {
  const location = place?.geometry?.location;
  if (!location) return null;

  const lat =
    typeof location.lat === 'function' ? location.lat() : location.lat;
  const lng =
    typeof location.lng === 'function' ? location.lng() : location.lng;
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;

  return { lat, lng };
};

const uploadAddressPreviewBlob = async (
  blob: Blob,
  filename: string,
): Promise<string> => {
  const file = new File([blob], filename, {
    type: blob.type || 'image/png',
  });

  try {
    const response = await generateUploadUrl({
      business_type: 'image',
      filename,
      custom_prefix: 'address-preview',
    });
    if (response.code !== 0 || !response.data) {
      throw new Error(response.message || '获取上传凭证失败');
    }

    const { upload_url, form_data } = response.data;
    const formData = new FormData();
    Object.keys(form_data).forEach((key) => {
      formData.append(key, form_data[key]);
    });
    formData.append('file', file);

    const uploadResponse = await fetch(upload_url, {
      method: 'POST',
      body: formData,
    });
    if (!uploadResponse.ok) {
      throw new Error(`上传失败: ${uploadResponse.statusText}`);
    }

    return response.data.file_url;
  } catch (error) {
    console.warn('地址预览图直传失败，降级服务端中转:', error);
    const response = await uploadFile(file, {
      business_type: 'image',
      custom_prefix: 'address-preview',
    });
    if (response.code === 0 && response.data?.file_url) {
      return response.data.file_url;
    }
    throw new Error(response.message || '上传失败');
  }
};

/**
 * Google 地图地点选择器组件
 *
 * 用于选择地点并填写详细地址的可复用组件
 *
 * 数据回显优先级（从高到低）：
 * 1. initialPlace（PlaceResult 对象，最高优先级）
 * 2. initialPlaceData.latitude/longitude（使用已保存的坐标和基础地址）
 * 3. initialPlaceData（使用序列化数据构建基本地点信息，最后回退）
 *
 * 输入框回填优先级：
 * - 优先使用 name 字段
 * - 如果没有 name，使用 formatted_address
 *
 * @example
 * ```tsx
 * // 基础使用
 * <GoogleMapLocationPicker
 *   onPlaceChange={(place) => console.log('地点变化:', place)}
 *   onDetailAddressChange={(address) => console.log('详细地址:', address)}
 * />
 *
 * // 数据回显（推荐使用 place_id）
 * <GoogleMapLocationPicker
 *   initialPlaceData={{
 *     place_id: 'ChIJ...',
 *     name: '地点名称',
 *     latitude: 39.9042,
 *     longitude: 116.4074,
 *     detail: '详细地址'
 *   }}
 *   initialDetailAddress="3楼301室"
 *   onPlaceChange={(place) => console.log('地点变化:', place)}
 *   onDetailAddressChange={(address) => console.log('详细地址:', address)}
 * />
 *
 * // 使用 StructuredAddress（推荐，组件内部自动转换）
 * <GoogleMapLocationPicker
 *   initialStructuredAddress={structuredAddress}
 *   onPlaceChange={(place) => console.log('地点变化:', place)}
 *   onDetailAddressChange={(address) => console.log('详细地址:', address)}
 * />
 *
 * // 预览模式（只读）
 * <GoogleMapLocationPicker
 *   disabled
 *   initialStructuredAddress={structuredAddress}
 * />
 * ```
 */
/**
 * 将 StructuredAddress 转换为 SerializedPlaceData（组件内部使用）
 */
const convertStructuredAddressToSerializedPlaceData = (
  structured: API.StructuredAddress,
): SerializedPlaceData => {
  // 处理 viewport（从 Record<string, any> 转换为标准格式）
  let viewport: SerializedPlaceData['viewport'] = null;
  if (structured.viewport && typeof structured.viewport === 'object') {
    const vp = structured.viewport as any;
    if (
      vp.south !== null &&
      vp.south !== undefined &&
      vp.west !== null &&
      vp.west !== undefined &&
      vp.north !== null &&
      vp.north !== undefined &&
      vp.east !== null &&
      vp.east !== undefined
    ) {
      viewport = {
        south: typeof vp.south === 'number' ? vp.south : null,
        west: typeof vp.west === 'number' ? vp.west : null,
        north: typeof vp.north === 'number' ? vp.north : null,
        east: typeof vp.east === 'number' ? vp.east : null,
      };
    }
  }

  // 构建 address_components（从结构化字段构建）
  const addressComponents: SerializedPlaceData['address_components'] = [];
  if (structured.street_number) {
    addressComponents.push({
      long_name: structured.street_number,
      short_name: structured.street_number,
      types: ['street_number'],
    });
  }
  if (structured.route) {
    addressComponents.push({
      long_name: structured.route,
      short_name: structured.route,
      types: ['route'],
    });
  }
  if (structured.sublocality) {
    addressComponents.push({
      long_name: structured.sublocality,
      short_name: structured.sublocality,
      types: ['sublocality'],
    });
  }
  if (structured.locality) {
    addressComponents.push({
      long_name: structured.locality,
      short_name: structured.locality,
      types: ['locality'],
    });
  }
  if (structured.administrative_area_level_1) {
    addressComponents.push({
      long_name: structured.administrative_area_level_1,
      short_name: structured.administrative_area_level_1,
      types: ['administrative_area_level_1'],
    });
  }
  if (structured.administrative_area_level_2) {
    addressComponents.push({
      long_name: structured.administrative_area_level_2,
      short_name: structured.administrative_area_level_2,
      types: ['administrative_area_level_2'],
    });
  }
  if (structured.country) {
    addressComponents.push({
      long_name: structured.country,
      short_name: structured.country,
      types: ['country'],
    });
  }
  if (structured.postal_code) {
    addressComponents.push({
      long_name: structured.postal_code,
      short_name: structured.postal_code,
      types: ['postal_code'],
    });
  }

  return {
    place_id: structured.place_id || null,
    name: structured.name || null,
    formatted_address: structured.formatted_address || null,
    vicinity: structured.vicinity || null,
    latitude: structured.latitude || null,
    longitude: structured.longitude || null,
    viewport,
    address_components: addressComponents.length > 0 ? addressComponents : null,
    detail: structured.detail || null,
  };
};

const GoogleMapLocationPicker = React.forwardRef<
  GoogleMapLocationPickerHandle,
  GoogleMapLocationPickerProps
>((props, ref) => {
  const intl = useIntl();
  const {
    onPlaceChange,
    onAddressPreviewUrlChange,
    onAddressPreviewUploadingChange,
    onDetailAddressChange,
    initialPlace = null,
    initialDetailAddress = '',
    initialPlaceData = null,
    initialStructuredAddress = null,
    disabled = false,
  } = props;

  // 根据当前应用语言解析默认地图语言（zh_CN/zh-CN -> zh-CN, en_US/en-US -> en, km_KH/km-KH -> km）
  const getDefaultLanguage = useCallback((): LanguageCode => {
    const loc = (intl.locale || '').toLowerCase();
    if (loc.includes('zh')) return 'zh-CN';
    if (loc.includes('km')) return 'km';
    return 'en';
  }, [intl.locale]);

  // 状态管理（地区默认柬埔寨，语言默认跟随当前应用语言）
  const [selectedPlace, setSelectedPlace] =
    useState<google.maps.places.PlaceResult | null>(initialPlace);
  const [mapCenter, setMapCenter] =
    useState<google.maps.LatLngLiteral>(defaultCenter);
  const [mapZoom, setMapZoom] = useState(15);
  const [detailAddress, setDetailAddress] = useState(initialDetailAddress);
  const [language, setLanguage] = useState<LanguageCode>(getDefaultLanguage);
  const [region, setRegion] = useState<RegionCode>('kh');
  const [placeInputValue, setPlaceInputValue] = useState('');
  const [_isMapLoaded, setIsMapLoaded] = useState(false);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const addressPreviewUploadSeqRef = useRef(0);

  // Refs
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  // 获取 Google Maps API Key
  const apiKey = process.env.GOOGLE_MAPS_API_KEY || '';

  const uploadAddressPreview = useCallback(
    async (
      place: google.maps.places.PlaceResult,
      zoom: number = 15,
    ): Promise<string | null> => {
      const location = getPlaceLocation(place);
      if (!location || !apiKey) {
        onAddressPreviewUrlChange?.(null);
        return null;
      }

      const seq = addressPreviewUploadSeqRef.current + 1;
      addressPreviewUploadSeqRef.current = seq;
      onAddressPreviewUrlChange?.(null);
      onAddressPreviewUploadingChange?.(true);
      setIsGeneratingPreview(true);

      try {
        const params = new URLSearchParams({
          key: apiKey,
          center: `${location.lat},${location.lng}`,
          zoom: String(zoom),
          size: '640x500',
          scale: '2',
          maptype: 'roadmap',
          language,
          region: region.toUpperCase(),
        });
        params.append('markers', `color:red|${location.lat},${location.lng}`);

        const response = await fetch(
          `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`,
        );
        if (!response.ok) {
          throw new Error(`Static Maps request failed: ${response.statusText}`);
        }

        const blob = await response.blob();
        const url = await uploadAddressPreviewBlob(
          blob,
          `address-preview-${Date.now()}.png`,
        );

        if (addressPreviewUploadSeqRef.current === seq) {
          onAddressPreviewUrlChange?.(url);
          message.success(
            intl.formatMessage({ id: '组件.地址.预览图生成成功' }),
          );
        }
        return url;
      } catch (error) {
        console.error('生成地址预览图失败:', error);
        if (addressPreviewUploadSeqRef.current === seq) {
          onAddressPreviewUrlChange?.(null);
          message.warning(
            intl.formatMessage({ id: '组件.地址.预览图生成失败' }),
          );
        }
        return null;
      } finally {
        if (addressPreviewUploadSeqRef.current === seq) {
          onAddressPreviewUploadingChange?.(false);
          setIsGeneratingPreview(false);
        }
      }
    },
    [
      apiKey,
      intl,
      language,
      onAddressPreviewUploadingChange,
      onAddressPreviewUrlChange,
      region,
    ],
  );

  useImperativeHandle(
    ref,
    () => ({
      ensureAddressPreview: async () => {
        if (!selectedPlace) return null;
        return uploadAddressPreview(selectedPlace, mapZoom);
      },
    }),
    [mapZoom, selectedPlace, uploadAddressPreview],
  );

  const buildBasicPlaceFromGeocode = useCallback(
    (
      result: google.maps.GeocoderResult | null,
      location: google.maps.LatLngLiteral,
    ): google.maps.places.PlaceResult => ({
      place_id: result?.place_id || undefined,
      name: result?.formatted_address || `${location.lat}, ${location.lng}`,
      formatted_address:
        result?.formatted_address || `${location.lat}, ${location.lng}`,
      address_components: result?.address_components || [],
      geometry: {
        location: new window.google.maps.LatLng(location.lat, location.lng),
        viewport: result?.geometry.viewport || undefined,
      },
      types: result?.types || undefined,
    }),
    [],
  );

  const applyPlaceSelection = useCallback(
    (place: google.maps.places.PlaceResult, showMessage = true) => {
      const location = getPlaceLocation(place);
      if (!location) return;

      setSelectedPlace(place);
      setMapCenter(location);
      setMapZoom(15);
      setPlaceInputValue(
        place.name ||
          place.formatted_address ||
          `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`,
      );
      onAddressPreviewUrlChange?.(null);

      const countryComponent = place.address_components?.find((comp) =>
        comp.types?.includes('country'),
      );
      const countryCode = countryComponent?.short_name?.toLowerCase();
      if (
        countryCode === 'cn' ||
        countryCode === 'us' ||
        countryCode === 'kh'
      ) {
        setRegion(countryCode as RegionCode);
      }

      onPlaceChange?.(place);
      if (showMessage) {
        message.success(intl.formatMessage({ id: '组件.地址.地点已选择' }));
      }
    },
    [intl, onAddressPreviewUrlChange, onPlaceChange],
  );

  const updateAutocompleteBounds = useCallback(() => {
    if (!autocompleteRef.current || !window.google?.maps) {
      return;
    }

    const mapBounds = mapRef.current?.getBounds();
    if (mapBounds) {
      autocompleteRef.current.setBounds(mapBounds);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    const radius = 0.45; // 约 50km
    bounds.extend(
      new window.google.maps.LatLng(
        mapCenter.lat - radius,
        mapCenter.lng - radius,
      ),
    );
    bounds.extend(
      new window.google.maps.LatLng(
        mapCenter.lat + radius,
        mapCenter.lng + radius,
      ),
    );
    autocompleteRef.current.setBounds(bounds);
  }, [mapCenter]);

  const handleAutocompleteLoad = useCallback(
    (autocomplete: google.maps.places.Autocomplete) => {
      autocompleteRef.current = autocomplete;
      updateAutocompleteBounds();
    },
    [updateAutocompleteBounds],
  );

  const handleAddressSearch = useCallback(
    async (value?: string) => {
      const keyword = (value ?? placeInputValue).trim();
      if (disabled || !googleMapsLoaded || !window.google?.maps || !keyword) {
        return;
      }

      setIsSearching(true);
      try {
        const geocoder = new window.google.maps.Geocoder();
        const results = await new Promise<google.maps.GeocoderResult[] | null>(
          (resolve) => {
            geocoder.geocode(
              {
                address: keyword,
                region,
                bounds: mapRef.current?.getBounds() || undefined,
              },
              (geocodeResults, status) => {
                if (status === window.google.maps.GeocoderStatus.OK) {
                  resolve(geocodeResults || []);
                  return;
                }
                resolve(null);
              },
            );
          },
        );
        const result = results?.[0];
        const location = result?.geometry.location;
        if (!result || !location) {
          message.warning(intl.formatMessage({ id: '组件.地址.未找到地址' }));
          return;
        }

        applyPlaceSelection(
          buildBasicPlaceFromGeocode(result, {
            lat: location.lat(),
            lng: location.lng(),
          }),
        );
      } finally {
        setIsSearching(false);
      }
    },
    [
      applyPlaceSelection,
      buildBasicPlaceFromGeocode,
      disabled,
      googleMapsLoaded,
      intl,
      placeInputValue,
      region,
    ],
  );

  const handleAutocompletePlaceChanged = useCallback(() => {
    if (disabled || !autocompleteRef.current) {
      return;
    }

    const place = autocompleteRef.current.getPlace();
    if (!place?.geometry?.location) {
      void handleAddressSearch(place?.name);
      return;
    }

    applyPlaceSelection(place);
  }, [applyPlaceSelection, disabled, handleAddressSearch]);

  // 地图点击选点（与搜索选择等价）
  const handleMapClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (
        disabled ||
        !googleMapsLoaded ||
        !window.google?.maps ||
        !event.latLng
      ) {
        return;
      }

      const clickedLat = event.latLng.lat();
      const clickedLng = event.latLng.lng();

      // 地图点击只做基础反向地理编码，不再按 POI placeId 请求 Places Details。
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { location: { lat: clickedLat, lng: clickedLng } },
        (results, status) => {
          if (
            status === window.google.maps.GeocoderStatus.OK &&
            results &&
            results[0]
          ) {
            applyPlaceSelection(
              buildBasicPlaceFromGeocode(results[0], {
                lat: clickedLat,
                lng: clickedLng,
              }),
            );
            return;
          }

          applyPlaceSelection({
            name: `${clickedLat.toFixed(6)}, ${clickedLng.toFixed(6)}`,
            formatted_address: `${clickedLat.toFixed(6)}, ${clickedLng.toFixed(6)}`,
            geometry: {
              location: new window.google.maps.LatLng(clickedLat, clickedLng),
            },
          });
        },
      );
    },
    [
      applyPlaceSelection,
      buildBasicPlaceFromGeocode,
      disabled,
      googleMapsLoaded,
    ],
  );

  // 地图加载完成回调
  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setIsMapLoaded(true);
    setGoogleMapsLoaded(true);
  }, []);

  // 语言切换
  const handleLanguageChange = (value: LanguageCode) => {
    setLanguage(value);
  };

  // 地区切换
  const handleRegionChange = (value: RegionCode) => {
    setRegion(value);
    const regionConfig = REGION_CONFIGS[value];
    setMapCenter(regionConfig.center);
    setMapZoom(10);
    setSelectedPlace(null);
    setPlaceInputValue('');
    onAddressPreviewUrlChange?.(null);
  };

  // 详细地址变化
  const handleDetailAddressChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (disabled) {
      return;
    }
    const value = e.target.value;
    setDetailAddress(value);
    onDetailAddressChange?.(value);
  };

  // 从序列化数据构建 PlaceResult（用于回显，当无法获取完整信息时）
  const buildPlaceFromSerializedData = useCallback(
    (data: SerializedPlaceData): google.maps.places.PlaceResult | null => {
      if (!data.latitude || !data.longitude) {
        return null;
      }

      if (!googleMapsLoaded || !window.google?.maps) {
        return null;
      }

      const location = new window.google.maps.LatLng(
        data.latitude,
        data.longitude,
      );

      // 构建 viewport（如果有）
      let viewport: google.maps.LatLngBounds | undefined;
      if (data.viewport) {
        const { south, west, north, east } = data.viewport;
        if (
          south !== null &&
          south !== undefined &&
          west !== null &&
          west !== undefined &&
          north !== null &&
          north !== undefined &&
          east !== null &&
          east !== undefined
        ) {
          viewport = new window.google.maps.LatLngBounds();
          viewport.extend(new window.google.maps.LatLng(south, west));
          viewport.extend(new window.google.maps.LatLng(north, east));
        }
      }

      // 转换 address_components 为正确的类型
      const addressComponents: google.maps.GeocoderAddressComponent[] =
        data.address_components
          ?.filter(
            (comp) =>
              comp.long_name !== undefined ||
              comp.short_name !== undefined ||
              comp.types !== undefined,
          )
          .map((comp) => ({
            long_name: comp.long_name || '',
            short_name: comp.short_name || '',
            types: comp.types || [],
          })) || [];

      const place: google.maps.places.PlaceResult = {
        place_id: data.place_id || undefined,
        name: data.name || undefined,
        formatted_address: data.formatted_address || undefined,
        vicinity: data.vicinity || undefined,
        address_components: addressComponents,
        geometry: {
          location,
          viewport: viewport || undefined,
        },
      };

      return place;
    },
    [googleMapsLoaded],
  );

  // 组件销毁时清理资源
  useEffect(() => {
    return () => {
      autocompleteRef.current = null;
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    updateAutocompleteBounds();
  }, [updateAutocompleteBounds]);

  // 数据回显：实现优先使用和回退机制
  useEffect(() => {
    // 如果 Google Maps 还未加载，等待加载完成
    if (!googleMapsLoaded) {
      return;
    }

    const loadInitialData = async () => {
      let place: google.maps.places.PlaceResult | null = null;
      let placeName = '';
      let location: { lat: number; lng: number } | null = null;

      // 如果传入了 initialStructuredAddress，先转换为 initialPlaceData
      let effectivePlaceData = initialPlaceData;
      if (initialStructuredAddress) {
        effectivePlaceData = convertStructuredAddressToSerializedPlaceData(
          initialStructuredAddress,
        );
      }

      // 优先级1：使用 initialPlace（PlaceResult 对象，最高优先级）
      if (initialPlace) {
        place = initialPlace;
        placeName = initialPlace.name || initialPlace.formatted_address || '';
        if (initialPlace.geometry?.location) {
          const loc = initialPlace.geometry.location;
          const latValue = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
          const lngValue = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
          if (typeof latValue === 'number' && typeof lngValue === 'number') {
            location = {
              lat: latValue,
              lng: lngValue,
            };
          }
        }
      }
      // 优先级2：使用序列化数据构建基本地点信息，不再根据 place_id 或坐标请求 Google
      else if (effectivePlaceData) {
        const fallbackPlace = buildPlaceFromSerializedData(effectivePlaceData);
        if (fallbackPlace) {
          place = fallbackPlace;
          placeName =
            effectivePlaceData.name ||
            effectivePlaceData.formatted_address ||
            '';
          if (effectivePlaceData.latitude && effectivePlaceData.longitude) {
            location = {
              lat: effectivePlaceData.latitude,
              lng: effectivePlaceData.longitude,
            };
          }
        }
      }

      // 更新状态
      if (place) {
        setSelectedPlace(place);
        if (placeName) {
          setPlaceInputValue(placeName);
        }
        if (location) {
          setMapCenter(location);
          setMapZoom(15);
        }

        // 根据地点自动调整地区偏好
        if (place.address_components) {
          const countryComponent = place.address_components.find((comp) =>
            comp.types?.includes('country'),
          );
          if (countryComponent) {
            const countryCode = countryComponent.short_name?.toLowerCase();
            if (countryCode === 'cn') {
              setRegion('cn');
            } else if (countryCode === 'us') {
              setRegion('us');
            } else if (countryCode === 'kh') {
              setRegion('kh');
            }
          }
        }
      }

      // 更新详细地址（只有当明确传入时才更新）
      if (initialDetailAddress !== undefined && initialDetailAddress !== '') {
        setDetailAddress(initialDetailAddress);
      } else if (effectivePlaceData?.detail) {
        // 如果 effectivePlaceData 中有 detail 字段，也使用它
        setDetailAddress(effectivePlaceData.detail);
      }
    };

    loadInitialData();
  }, [
    initialPlace,
    initialPlaceData,
    initialStructuredAddress,
    initialDetailAddress,
    googleMapsLoaded,
    buildPlaceFromSerializedData,
  ]);

  // 使用 useMemo 稳定 LoadScript 的 key
  const loadScriptKey = useMemo(() => `google-maps-${apiKey}`, [apiKey]);

  // 使用 useMemo 稳定 loadingElement
  const loadingElement = useMemo(
    () => (
      <div style={{ height: '500px' }}>
        {intl.formatMessage({ id: '组件.地址.加载地图中' })}
      </div>
    ),
    [intl],
  );

  const placeFirstLabel = intl.formatMessage({ id: '组件.地址.请先选择地点' });
  // 构建预览地址文本
  const previewAddress = useMemo(() => {
    if (!selectedPlace) {
      return placeFirstLabel;
    }

    const parts: string[] = [];

    // vicinity（附近区域）
    if (selectedPlace.vicinity) {
      parts.push(selectedPlace.vicinity);
    }

    // name（地点名称）
    if (selectedPlace.name) {
      parts.push(selectedPlace.name);
    }

    // 详细地址
    if (detailAddress) {
      parts.push(detailAddress);
    }

    return parts.length > 0 ? parts.join(' · ') : placeFirstLabel;
  }, [selectedPlace, detailAddress, placeFirstLabel]);

  if (!apiKey) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: 'red', fontSize: '16px' }}>
            {intl.formatMessage({ id: '组件.地址.未配置GoogleMapsAPIKey' })}
          </p>
        </div>
      </Card>
    );
  }

  return (
    <LoadScript
      key={loadScriptKey}
      googleMapsApiKey={apiKey}
      language={language}
      region={region}
      libraries={googleMapsLibraries}
      loadingElement={loadingElement}
      onLoad={() => {
        setGoogleMapsLoaded(true);
      }}
    >
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* 顶部配置区域 - 简化展示 */}
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <Space size="middle">
                <Space size="small">
                  <GlobalOutlined style={{ color: '#999' }} />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {intl.formatMessage({ id: '组件.地址.语言' })}
                  </Text>
                  <Select
                    value={language}
                    onChange={handleLanguageChange}
                    disabled={disabled}
                    style={{ width: 100 }}
                    size="small"
                    options={Object.entries(LANGUAGE_CONFIGS).map(
                      ([key, config]) => ({
                        value: key,
                        label: config.label,
                      }),
                    )}
                  />
                </Space>
                <Divider type="vertical" />
                <Space size="small">
                  <EnvironmentOutlined style={{ color: '#999' }} />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {intl.formatMessage({ id: '组件.地址.地区' })}
                  </Text>
                  <Select
                    value={region}
                    onChange={handleRegionChange}
                    disabled={disabled}
                    style={{ width: 100 }}
                    size="small"
                    options={Object.entries(REGION_CONFIGS).map(
                      ([key, config]) => ({
                        value: key,
                        label: config.label,
                      }),
                    )}
                  />
                </Space>
              </Space>
            </Col>
          </Row>

          {/* 主要内容区域：左右布局 */}
          <Row gutter={24}>
            {/* 左侧：地点选择和详细地址 */}
            <Col span={14}>
              <Space
                direction="vertical"
                size="middle"
                style={{ width: '100%' }}
              >
                {/* 地点输入框 */}
                <Card
                  size="small"
                  title={intl.formatMessage({ id: '组件.地址.地点选择' })}
                >
                  {disabled ? (
                    <Input
                      placeholder={intl.formatMessage({
                        id: '组件.地址.请输入地点名称或地址',
                      })}
                      value={placeInputValue}
                      disabled
                      prefix={<EnvironmentOutlined />}
                      size="large"
                    />
                  ) : (
                    <Autocomplete
                      onLoad={handleAutocompleteLoad}
                      onPlaceChanged={handleAutocompletePlaceChanged}
                      fields={autocompleteFields}
                      restrictions={{ country: region }}
                    >
                      <Input.Search
                        placeholder={intl.formatMessage({
                          id: '组件.地址.请输入地点名称或地址',
                        })}
                        value={placeInputValue}
                        onChange={(e) => setPlaceInputValue(e.target.value)}
                        onSearch={handleAddressSearch}
                        enterButton={
                          <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            loading={isSearching}
                          >
                            {intl.formatMessage({ id: '组件.地址.搜索地址' })}
                          </Button>
                        }
                        prefix={<EnvironmentOutlined />}
                        size="large"
                      />
                    </Autocomplete>
                  )}
                </Card>

                {/* 详细地址输入框 */}
                <Card
                  size="small"
                  title={intl.formatMessage({ id: '组件.地址.详细地址' })}
                >
                  <Input
                    placeholder={intl.formatMessage({
                      id: '组件.地址.请输入详细地址如3楼301室',
                    })}
                    value={detailAddress}
                    onChange={handleDetailAddressChange}
                    disabled={disabled}
                    size="large"
                  />
                </Card>

                {/* 预览区域 */}
                <Card
                  size="small"
                  title={
                    <>
                      <EyeOutlined />{' '}
                      {intl.formatMessage({ id: '组件.地址.地址预览' })}
                    </>
                  }
                >
                  <Paragraph
                    style={{
                      margin: 0,
                      padding: '12px',
                      background: '#f5f5f5',
                      borderRadius: '4px',
                      minHeight: '60px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Text>{previewAddress}</Text>
                  </Paragraph>

                  {selectedPlace?.formatted_address && (
                    <div style={{ marginTop: '12px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {intl.formatMessage({ id: '组件.地址.标准地址' })}
                      </Text>
                      <Text style={{ fontSize: '12px', marginLeft: '8px' }}>
                        {selectedPlace.formatted_address}
                      </Text>
                    </div>
                  )}

                  {!disabled && (
                    <Button
                      block
                      icon={<SaveOutlined />}
                      loading={isGeneratingPreview}
                      disabled={!selectedPlace}
                      style={{ marginTop: 12 }}
                      onClick={() => {
                        if (!selectedPlace) return;
                        void uploadAddressPreview(selectedPlace, mapZoom);
                      }}
                    >
                      {intl.formatMessage({ id: '组件.地址.生成地图图片' })}
                    </Button>
                  )}
                </Card>
              </Space>
            </Col>

            {/* 右侧：地图显示 */}
            <Col span={10}>
              <Card
                size="small"
                title={intl.formatMessage({ id: '组件.地址.地图显示' })}
              >
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={mapCenter}
                  zoom={mapZoom}
                  onLoad={onMapLoad}
                  onClick={handleMapClick}
                  options={{
                    mapTypeControl: !disabled,
                    streetViewControl: !disabled,
                    fullscreenControl: !disabled,
                    draggable: !disabled,
                    zoomControl: !disabled,
                    scrollwheel: !disabled,
                    disableDoubleClickZoom: disabled,
                  }}
                >
                  {selectedPlace?.geometry?.location && (
                    <Marker
                      position={{
                        lat: selectedPlace.geometry.location.lat(),
                        lng: selectedPlace.geometry.location.lng(),
                      }}
                      title={
                        selectedPlace.name || selectedPlace.formatted_address
                      }
                    />
                  )}
                </GoogleMap>
              </Card>
            </Col>
          </Row>
        </Space>
      </Card>
    </LoadScript>
  );
});

export default GoogleMapLocationPicker;
